import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "../../components/ui/sheet";
import { Checkbox } from "../../components/ui/checkbox";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";
import { useAccessDefinition } from "../../hooks/user/useAdminAccess";
import { useRoleAccess } from "../../hooks/user/useRoleAccess";
import { useUpdateRoleAccess } from "../../hooks/user/useUpdateRoleAccess";
import { useRoles } from "../../hooks/user/useRoles";

import { Edit2, Lock, Search, Shield } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import LoadingIndicator from "../../components/LoadingIndicator";
import { Skeleton } from "../../components/ui/skeleton";
import { toUserMessage } from "../../utils/toUserMessage";
import Alert from "../../components/ui/Alert";

type PermissionDefinition = {
  key: string;
  displayName: string;
  group: string;
  description?: string | null;
};

function PermissionList({
  definitions,
  selected,
  onToggle,
}: {
  definitions: PermissionDefinition[];
  selected: string[];
  onToggle: (key: string, checked: boolean) => void;
}) {
  const grouped = useMemo(() => {
    return (definitions ?? []).reduce<Record<string, PermissionDefinition[]>>(
      (acc, perm) => {
        if (!acc[perm.group]) acc[perm.group] = [];
        acc[perm.group].push(perm);
        return acc;
      },
      {}
    );
  }, [definitions]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([group, perms]) => (
        <div key={group} className="border rounded-lg bg-white shadow-sm">
          <div className="px-4 py-2 border-b bg-slate-50 text-sm font-semibold text-slate-700">
            {group}
          </div>
          <div className="divide-y">
            {perms.map((perm) => {
              const checked = selectedSet.has(perm.key);

              return (
                <div
                  key={perm.key}
                  onClick={() => onToggle(perm.key, !checked)}
                  className={cn(
                    "w-full text-left",
                    "flex items-center justify-between px-4 py-3 gap-4",
                    "transition-colors cursor-pointer",
                    "hover:bg-green-50 active:bg-slate-100",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  )}
                >
                  <div className="min-w-0">
                    <div className="select-none text-sm font-medium text-slate-800">
                      {perm.displayName}
                    </div>
                    {perm.description && (
                      <div className="select-none text-xs text-slate-500">
                        {perm.description}
                      </div>
                    )}
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedSet.has(perm.key)}
                      onCheckedChange={(v) => onToggle(perm.key, v === true)}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RoleListPage() {
  const { toast } = useToast();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any | null>(null);

  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  const defQ = useAccessDefinition();
  const rolesQ = useRoles();
  const roleAccessQ = useRoleAccess(selectedRole?.id);
  const updateRoleAccess = useUpdateRoleAccess();

  const isSaving = updateRoleAccess.isPending;
  const isLoadingList = rolesQ.isPending;
  const isLoadingAccess = roleAccessQ.isPending;

  useEffect(() => {
    if (roleAccessQ.data) {
      setEditPermissions((roleAccessQ.data as any).permissions ?? []);
    }
  }, [roleAccessQ.data]);

  const permissionDefs: PermissionDefinition[] =
    (defQ.data as any)?.permissions ?? [];

  const handleEditRole = (role: any) => {
    setSelectedRole(role);
    setIsSheetOpen(true);
    setEditPermissions([]);
  };

  const handlePermissionToggle = (key: string, checked: boolean) => {
    setEditPermissions((prev) => {
      const set = new Set(prev);
      if (checked) set.add(key);
      else set.delete(key);
      return Array.from(set);
    });
  };

  const handleSave = async () => {
    if (!selectedRole?.id) return;

    try {
      await updateRoleAccess.mutateAsync({
        roleId: selectedRole.id,
        permissions: editPermissions,
      });

      toast({
        variant: "success",
        title: "Cập nhật thành công",
        description: "",
        duration: 2000,
      });

    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: "Lưu thông tin thất bại",
        description: errorMsg,
        duration: 2000,
      });
    }
  };

  const isLoading = defQ.isPending || rolesQ.isPending;

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setSelectedRole(null);
      setEditPermissions([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b-2 border-green-600 p-4 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Phân quyền cho vai trò
          </h2>
        </div>
      </div>

      <div className="p-6 flex flex-col gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vai trò</TableHead>
                <TableHead className="text-right w-[120px]">
                  Hành động
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingList ? (
                Array.from({ length: 4 }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-row-${rowIndex}`}>
                    {Array.from({ length: 2 }).map((_, colIndex) => (
                      <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                        <Skeleton className="h-10 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !rolesQ.data || (rolesQ.data as any[]).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-center text-slate-400 py-8"
                  >
                    Không có có vai trò nào.
                  </TableCell>
                </TableRow>
              ) : (
                (rolesQ.data as any[] | undefined)?.map((role) => (
                  <TableRow
                    key={role.id ?? role.name}
                    className=""
                    onClick={() => handleEditRole(role)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="text-black font-medium">
                            {role.name}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditRole(role)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl px-0 pt-0 pb-[70px]">
          <div className="relative h-full">
            <div className="h-full overflow-y-auto p-6">
              <SheetHeader className="mb-6">
                <SheetTitle>Thiết lập quyền vai trò</SheetTitle>
                <SheetDescription>
                  <Alert
                    variant="info"
                    msg="Quyền này là mặc định của vai trò."
                  />
                </SheetDescription>
              </SheetHeader>

              {selectedRole && (
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-brand flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Thông tin Vai trò
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Input
                          className="text-black font-medium"
                          value={selectedRole.name ?? ""}
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-brand flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Quyền của vai trò
                      </h3>
                      <Badge variant="default" className="font-normal">
                        {selectedRole.name}
                      </Badge>
                    </div>

                    <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 text-sm text-slate-600">
                      {isLoadingAccess
                        ? "Đang tải quyền vai trò..."
                        : `Đã chọn ${editPermissions.length} quyền.`}
                    </div>

                    <PermissionList
                      definitions={permissionDefs}
                      selected={editPermissions}
                      onToggle={handlePermissionToggle}
                    />
                  </div>
                </div>
              )}

              <SheetFooter className="mt-8 h-[60px] fixed bottom-0 flex flex-row gap-2 justify-end items-center w-full sm:max-w-2xl right-0 bg-white px-4 py-2 border-t border-t-primary">
                <SheetClose asChild>
                  <Button variant="outline">Hủy bỏ</Button>
                </SheetClose>
                <Button
                  onClick={handleSave}
                  className={cn("btn-primary", {
                    "opacity-60 cursor-not-allowed": isSaving,
                  })}
                  disabled={isSaving}
                >
                  Lưu thay đổi
                </Button>
              </SheetFooter>
            </div>
            {(isLoadingAccess || isSaving) && (
              <div className="absolute inset-0 z-50 touch-none overscroll-contain">
                <div
                  className="absolute inset-0 bg-white/40"
                  aria-hidden="true"
                />
                <div className="relative w-full h-full flex items-center justify-center">
                  <LoadingIndicator />
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
